import { API_BASE_URL } from '../config/authConfig';

/**
 * API client with authentication
 */
class ApiClient {
  private baseUrl: string;
  private getToken: (() => Promise<string | null>) | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Set the token getter function (called from useAuth)
   */
  setTokenGetter(getter: () => Promise<string | null>) {
    this.getToken = getter;
  }

  /**
   * Make an authenticated API request
   */
  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Add auth token if available
    if (this.getToken) {
      const token = await this.getToken();
      if (token) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
      }
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      ...options,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(data.error || 'Request failed', response.status, data.errors);
    }

    return data;
  }

  async get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path);
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body);
  }

  async put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PUT', path, body);
  }

  async delete<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path);
  }
}

/**
 * Custom API error class
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public errors?: string[]
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Type definitions for API responses
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  errors?: string[];
}

export interface ListResponse<T> {
  success: boolean;
  data: T[];
  count: number;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

// API helper functions

/**
 * Search for users
 */
export async function searchUsers(query: string): Promise<any[]> {
  const response = await apiClient.get<ApiResponse<any[]>>(
    `/users/search?q=${encodeURIComponent(query)}`
  );
  return response.data || [];
}

/**
 * Get full user details including groups and manager
 */
export async function getUserDetails(userId: string): Promise<any> {
  const response = await apiClient.get<ApiResponse<any>>(
    `/employees/${userId}/details`
  );
  return response.data;
}

/**
 * Update employee profile
 */
export interface UpdateEmployeeProfilePayload {
  userId: string;
  userDisplayName: string;
  userPrincipalName: string;
  changes?: Record<string, any>;
  groupsToAdd?: string[];
  groupsToRemove?: string[];
  requestedSoftware?: string[];
  beforeSnapshot: Record<string, unknown>;
  afterSnapshot: Record<string, unknown>;
}

export async function updateEmployeeProfile(
  payload: UpdateEmployeeProfilePayload
): Promise<any> {
  const response = await apiClient.post<ApiResponse<any>>(
    '/employees/update-profile',
    payload
  );
  return response.data;
}

/**
 * Create hardware request
 */
export interface CreateHardwareRequestPayload {
  userId: string;
  userDisplayName: string;
  userPrincipalName: string;
  mainHardware: string[];
  peripherals: string[];
  monitorQuantity: number;
  justification: string;
}

export async function createHardwareRequest(
  payload: CreateHardwareRequestPayload
): Promise<{ requestId: string }> {
  const response = await apiClient.post<{ success: boolean; requestId: string; message: string }>(
    '/hardware/request',
    payload
  );
  return { requestId: response.requestId };
}
